import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import JsonLd from '../../components/base/JsonLd';

export default function Services() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const servicesJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${siteUrl}/services#webpage`,
    url: `${siteUrl}/services`,
    name: 'Home Health Services Las Vegas | Nursing & Therapy',
    description: 'Comprehensive home health care services in Las Vegas: skilled nursing, physical therapy, occupational therapy, speech therapy, medical social services, and home health aide. Medicare certified.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    provider: { '@id': `${siteUrl}/#organization` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
      ],
    },
    hasPart: [
      { '@type': 'MedicalWebPage', name: 'Skilled Nursing', url: `${siteUrl}/services/skilled-nursing` },
      { '@type': 'MedicalWebPage', name: 'Physical Therapy', url: `${siteUrl}/services/physical-therapy` },
      { '@type': 'MedicalWebPage', name: 'Occupational Therapy', url: `${siteUrl}/services/occupational-therapy` },
      { '@type': 'MedicalWebPage', name: 'Speech Therapy', url: `${siteUrl}/services/speech-therapy` },
      { '@type': 'MedicalWebPage', name: 'Medical Social Services', url: `${siteUrl}/services/medical-social-services` },
      { '@type': 'MedicalWebPage', name: 'Home Health Aide', url: `${siteUrl}/services/home-health-aide` },
      { '@type': 'MedicalWebPage', name: 'Chronic Disease Management', url: `${siteUrl}/services/chronic-disease-management` },
      { '@type': 'MedicalWebPage', name: 'Post-Surgical Care', url: `${siteUrl}/services/post-surgical-care` },
      { '@type': 'MedicalWebPage', name: 'Transitional Care', url: `${siteUrl}/services/transitional-care` },
    ],
  };

  useEffect(() => {
    document.title = 'Home Health Services Las Vegas | Nursing & Therapy';
  }, []);

  const mainServices = [
    {
      icon: 'ri-nurse-line',
      title: 'Skilled Nursing',
      href: '/services/skilled-nursing',
      desc: 'Our licensed registered nurses (RNs) and licensed practical nurses (LPNs) provide expert medical care in your home.',
      img: 'https://public.readdy.ai/ai/img_res/81d667513b4b16c436f70aec679c539f.jpg',
      items: ['Wound care and dressing changes', 'IV therapy and medication administration', 'Vital signs monitoring and assessment', 'Catheter care and management', 'Disease education and management', 'Pain management strategies'],
    },
    {
      icon: 'ri-walk-line',
      title: 'Physical Therapy',
      href: '/services/physical-therapy',
      desc: 'Our licensed physical therapists create personalized rehabilitation programs to help you regain strength, mobility, and independence.',
      img: 'https://readdy.ai/api/search-image?query=physical-therapist-helping-elderly-patient-practice-walking-exercises-using-walker-in-bright-modern-home-hallway-rehabilitation-professional-coaching-warm-natural-light&width=600&height=320&seq=svc-pt-2026&orientation=landscape',
      items: ['Post-surgical rehabilitation', 'Balance and gait training', 'Strength and endurance building', 'Pain reduction techniques', 'Fall prevention strategies', 'Therapeutic exercises and stretching'],
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'Occupational Therapy',
      href: '/services/occupational-therapy',
      desc: 'Our occupational therapists help you maintain independence in daily activities and adapt your home environment for safety.',
      img: 'https://readdy.ai/api/search-image?query=occupational-therapist-helping-senior-woman-practice-daily-living-skills-in-bright-kitchen-home-setting-adaptive-equipment-professional-caring-natural-light&width=600&height=320&seq=svc-ot-2026&orientation=landscape',
      items: ['Activities of daily living (ADL) training', 'Adaptive equipment recommendations', 'Home safety assessments', 'Fine motor skill development', 'Cognitive rehabilitation', 'Energy conservation techniques'],
    },
    {
      icon: 'ri-chat-voice-line',
      title: 'Speech Therapy',
      href: '/services/speech-therapy',
      desc: 'Our speech-language pathologists provide specialized treatment for communication and swallowing disorders.',
      img: 'https://readdy.ai/api/search-image?query=speech-therapist-working-with-elderly-patient-speech-exercises-comfortable-home-living-room-warm-light-professional-caring-interaction-natural-daylight&width=600&height=320&seq=svc-st-2026&orientation=landscape',
      items: ['Speech and language therapy', 'Swallowing evaluation and treatment', 'Cognitive-communication therapy', 'Voice therapy', 'Stroke recovery support', 'Alternative communication methods'],
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Medical Social Services',
      href: '/services/medical-social-services',
      desc: 'Our medical social workers provide counseling and support for emotional, social, and financial challenges related to illness.',
      img: 'https://readdy.ai/api/search-image?query=compassionate-social-worker-meeting-with-elderly-patient-and-family-in-comfortable-home-living-room-supportive-conversation-warm-light-professional-caring&width=600&height=320&seq=svc-mss-2026&orientation=landscape',
      items: ['Emotional and psychological support', 'Community resource coordination', 'Financial assistance guidance', 'Family counseling and education', 'Advance care planning', 'Crisis intervention'],
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Home Health Aide',
      href: '/services/home-health-aide',
      desc: 'Our certified home health aides provide compassionate personal care assistance to help you maintain dignity and comfort.',
      img: 'https://readdy.ai/api/search-image?query=caring-home-health-aide-assisting-elderly-woman-with-morning-routine-bright-clean-bathroom-professional-gentle-assistance-natural-light-warm-atmosphere&width=600&height=320&seq=svc-hha-2026&orientation=landscape',
      items: ['Bathing and personal hygiene', 'Dressing and grooming assistance', 'Meal preparation and feeding', 'Light housekeeping', 'Medication reminders', 'Companionship and emotional support'],
    },
  ];

  const specialtyPrograms = [
    {
      href: '/services/chronic-disease-management',
      icon: 'ri-heart-pulse-line',
      title: 'Chronic Disease Management',
      desc: 'Comprehensive care for CHF, COPD, diabetes, hypertension, and other chronic conditions to reduce hospitalizations.',
    },
    {
      href: '/services/post-surgical-care',
      icon: 'ri-surgical-mask-line',
      title: 'Post-Surgical Care',
      desc: 'Expert recovery support after surgery to ensure optimal healing and prevent complications.',
    },
    {
      href: '/services/transitional-care',
      icon: 'ri-hospital-line',
      title: 'Transitional Care',
      desc: 'Smooth, coordinated transition from hospital to home with care coordination and 24/7 support.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={servicesJsonLd as Record<string, unknown>} id="json-ld-services" />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional-nurse-in-white-uniform-providing-home-health-care-to-elderly-patient-in-bright-comfortable-home-interior-warm-natural-light-medical-care-at-home&width=1440&height=500&seq=services-hero-2026&orientation=landscape"
            alt="Home Health Care Services"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900/98"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-xs font-semibold uppercase tracking-wider mb-6">
            <i className="ri-service-line"></i> Our Services
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
            Comprehensive<br />Home Health Care
          </h1>
          <p className="text-slate-300 text-xl max-w-3xl mx-auto">
            Professional medical care and therapeutic services delivered in the comfort and privacy of your own home. Medicare certified.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
            <a href="/contact" className="inline-flex items-center gap-2 px-7 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
              Request Care Today
            </a>
            <a href="tel:866-939-0330" className="inline-flex items-center gap-2 px-7 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line"></i> 866-939-0330
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              What We Offer
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Core Services</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">Expert care from licensed, experienced healthcare professionals — right in your home.</p>
          </div>

          <div className="space-y-16">
            {mainServices.map((service, i) => (
              <div
                key={i}
                className={`grid lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                <div className={`relative w-full h-72 rounded-2xl overflow-hidden ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <img src={service.img} alt={service.title} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent"></div>
                </div>

                <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-5">
                    <i className={`${service.icon} text-amber-700 text-xl`}></i>
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-4">{service.title}</h2>
                  <p className="text-slate-500 text-base mb-6 leading-relaxed">{service.desc}</p>
                  <ul className="grid sm:grid-cols-2 gap-2.5 mb-7">
                    {service.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-amber-700 text-xs"></i>
                        </div>
                        <span className="text-slate-600 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <a href={service.href} className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-amber-600 text-white rounded-xl font-semibold text-sm transition-all duration-300 cursor-pointer">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialty Programs */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              Specialty Programs
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Specialized Care Programs</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">Targeted programs designed for specific health conditions and recovery needs.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {specialtyPrograms.map((prog, i) => (
              <a
                key={i}
                href={prog.href}
                className="group bg-white p-8 rounded-2xl border border-stone-200 hover:border-amber-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              >
                <div className="w-14 h-14 bg-slate-900 group-hover:bg-amber-600 rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300">
                  <i className={`${prog.icon} text-amber-400 group-hover:text-white text-2xl transition-colors duration-300`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{prog.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-5">{prog.desc}</p>
                <span className="text-amber-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all duration-200">
                  Learn More <i className="ri-arrow-right-line"></i>
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="bg-slate-900 rounded-3xl p-12 flex flex-col lg:flex-row items-center gap-10">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-xs font-semibold uppercase tracking-wider mb-5">
                <i className="ri-shield-check-line"></i> Insurance & Coverage
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">We Accept Most Major Insurance Plans</h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                We are Medicare certified and work with most major insurance providers, including Medicaid and many private insurance plans. Our care coordinators will verify your benefits before care begins.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Medicare', 'Medicaid', 'United Healthcare', 'Aetna', 'Humana', 'BCBS'].map((ins) => (
                  <span key={ins} className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg text-sm font-medium">
                    {ins}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 text-center">
              <div className="w-24 h-24 bg-amber-600 rounded-3xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-medal-fill text-white text-4xl"></i>
              </div>
              <div className="text-white font-bold text-xl mb-1">5-Star Rated</div>
              <div className="text-slate-400 text-sm">CMS Quality Rating</div>
              <a href="/contact" className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer">
                Verify Your Benefits
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-amber-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-5">Ready to Get Started?</h2>
          <p className="text-amber-100 text-lg mb-8">Contact us today to learn how our services can help you or your loved one recover at home.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white hover:bg-stone-50 text-amber-700 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
              Request Care Now
            </a>
            <a href="tel:866-939-0330" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-amber-700 hover:bg-amber-800 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line"></i> 866-939-0330
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
